/**
 * `/requests` (TD-28 段階④、D-5/D-10): 汎用申請の一覧・新規作成・詳細。
 *
 * ドメイン固有のフォーム項目はコードに埋め込まず、request_type_field_definitions を
 * サーバーから取得して描画する (D-19「全フィールドをフォームビルダー化」の帰結)。
 * 休暇 (`/leave/*`) の統合は D-9 のカットオーバー (段階⑧) で行うため、本画面は現時点では
 * 勤務調整・個人情報変更・テナント独自申請を扱う。
 *
 * 設計: docs/design/td28-request-workflow-platform-detail.md §8 / §10
 */
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Undo2, RotateCcw } from 'lucide-react';
import { apiFetch, ApiClientError, API_BASE, handleUnauthorized } from '@/lib/api';
import { useAuthStore, hasPermission } from '@/stores/auth';
import { FIELD_CONTEXT_VIEWING_LABEL } from '@/lib/request-field-visibility-labels';
import { toast } from '@/stores/toast';
import { Button } from '@/components/Button';
import { Modal, ConfirmDialog } from '@/components/Modal';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  RequestFormFields, type FieldDefinition, type FieldCondition, type FieldFormula, type FormValues,
  type AttachmentItem, type AttachmentSummary, type AttachmentCallbacks,
} from './RequestFormFields';

// -----------------------------------------------------------------------------
// 使い勝手フィードバック増分⑧ (D-U26/D-U31): ファイル添付。multipart アップロード・content
// ダウンロードは apiFetch (Content-Type を application/json に強制する) が使えないため、
// DocumentDetailPage.tsx と同じ生 fetch + Authorization ヘッダのパターンを踏襲する。
// -----------------------------------------------------------------------------
const uploadRequestAttachment = async (
  requestInstanceId: string, fieldDefinitionId: string, file: File,
): Promise<AttachmentItem> => {
  const fd = new FormData();
  fd.set('field_definition_id', fieldDefinitionId);
  fd.set('file', file);
  const res = await fetch(`${API_BASE}/request-instances/${requestInstanceId}/attachments`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${localStorage.getItem('access_token') ?? ''}` },
    body: fd,
  });
  handleUnauthorized(res.status);
  const body = await res.json() as { success: boolean; data?: AttachmentItem; error?: { message: string } };
  if (!res.ok || !body.success || !body.data) throw new Error(body.error?.message ?? 'アップロードに失敗しました');
  return body.data;
};

const fetchRequestAttachmentBlob = async (requestInstanceId: string, attachmentId: string): Promise<Blob> => {
  const res = await fetch(`${API_BASE}/request-instances/${requestInstanceId}/attachments/${attachmentId}/content`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('access_token') ?? ''}` },
  });
  handleUnauthorized(res.status);
  if (!res.ok) throw new Error('ファイルを取得できませんでした');
  return res.blob();
};

const deleteRequestAttachment = (requestInstanceId: string, attachmentId: string): Promise<void> =>
  apiFetch(`/request-instances/${requestInstanceId}/attachments/${attachmentId}`, { method: 'DELETE' });

/** field_definition_id → 添付要約。requestInstanceId が null の間 (未保存の新規申請) は空の Map。 */
function useAttachmentsQuery(requestInstanceId: string | null) {
  const qc = useQueryClient();
  const query = useQuery<Map<string, AttachmentSummary>>({
    queryKey: ['request-instance-attachments', requestInstanceId],
    queryFn: async () => {
      const res = await apiFetch<{ attachments: Record<string, AttachmentSummary> }>(
        `/request-instances/${requestInstanceId}/attachments`,
      );
      return new Map(Object.entries(res.attachments));
    },
    enabled: requestInstanceId !== null,
  });
  const refresh = () => { void qc.invalidateQueries({ queryKey: ['request-instance-attachments', requestInstanceId] }); };
  return { attachmentsByField: query.data, refresh };
}

type AvailableType = {
  request_type_id: string; category: string; code: string; label: string;
  timing_mode: string; requires_approval: boolean;
};
type RequestRow = {
  request_instance_id: string; request_type_id: string;
  request_type_label: string | null; request_type_category: string | null;
  requester_employee_id: string; status: string;
  // 使い勝手フィードバック⑤a (D-U34): 差し戻された回数 (生涯累積カウンタ、再提出・再決裁でもリセットされない)。
  return_count: number;
  // 使い勝手フィードバック⑤b (QA Major/platform-reviewer HIGH/design-and-code M-1 の反映):
  // 「今まさに前段階へ戻された状態で再決裁待ちか」。GET /:id (詳細) でのみ返る。return_count>0 だけでは
  // 審査が正常に進行中でも古い差し戻し表示が残り続けてしまうため、この専用フラグで判定する。
  is_level_back_pending?: boolean;
  approval_request_id: string | null;
  submitted_at: string | null; decided_at: string | null; applied_at: string | null;
  apply_error: string | null; row_version: number;
  created_at: string; updated_at: string;
};
type FormSpecResponse = {
  request_type: { request_type_id: string; label: string; category: string; timing_mode: string; requires_approval: boolean };
  target_employee_id: string;
  fields: FieldDefinition[]; conditions: FieldCondition[]; formulas: FieldFormula[];
};
type DetailResponse = {
  request: RequestRow;
  fields: FieldDefinition[]; conditions: FieldCondition[]; formulas: FieldFormula[];
  values: FormValues;
  // TD-28 D-24/D-25: 項目レベルの表示制御の結果。サーバーが導出した閲覧文脈と、
  // 伏せた項目 / 完全に隠した項目の情報。
  display_context: 'self' | 'approver' | 'viewer';
  approval_level: number;
  masked_field_ids: string[];
  hidden_field_count: number;
  // 使い勝手フィードバック③ (D-U21): 表示はされるが編集はできない項目。
  non_editable_field_ids: string[];
  // TD-28 §18-1: 時季変更の申入れ (労基法39条5項)。対象外の申請種別では常に空配列。
  timing_changes: TimingChange[];
  can_request_timing_change: boolean;
};

type TimingChange = {
  change_request_id: string;
  proposed_start_date: string | null;
  proposed_end_date: string | null;
  business_reason: string;
  requested_at: string;
  employee_response: 'accepted' | 'declined' | null;
  responded_at: string | null;
  response_note: string | null;
};

// ⚠️ §8-55: 語彙は lib/request-field-visibility-labels.ts に集約する (設定画面と共有)
const CONTEXT_LABEL = FIELD_CONTEXT_VIEWING_LABEL;

const STATUS_LABEL: Record<string, string> = {
  // 使い勝手フィードバック⑤a (D-U14): 'returned' = 差し戻され申請者の編集待ち。
  draft: '下書き', submitted: '承認待ち', returned: '差し戻し', approved: '承認済み', rejected: '却下',
  applied: '反映済み', apply_failed: '反映失敗', withdrawn: '取下げ',
};

/** TD-28 §17-1 (D-30): 所定労働日の自動判定。失敗しても申請自体は続けられるよう null を返す
 *  (判定できないことは正常な状態であり、手入力の経路は常に生きている)。 */
const resolveWorkDays = async (
  start: string, end: string, employeeId?: string,
): Promise<{ workDays: string[]; fromStandingPattern: number } | null> => {
  const params = new URLSearchParams({ start, end });
  if (employeeId) params.set('employee_id', employeeId);
  try {
    const res = await apiFetch<{
      work_days: string[];
      days: Array<{ date: string; is_work_day: boolean; basis: string }>;
    }>(`/request-instances/scheduled-work-days?${params.toString()}`);
    // ⚠️ basis を捨てない: 確定シフトから決まった日と、基本の勤務パターンから推定した日では
    //    確からしさが違う。推定分があることを申請者に伝えないと、そのまま提出されて
    //    実際と違う日数が消化される (design-and-code MEDIUM-5)。
    return {
      workDays: res.work_days,
      fromStandingPattern: res.days.filter((d) => d.is_work_day && d.basis === 'standing_pattern').length,
    };
  } catch {
    return null;
  }
};
const STATUS_CLS: Record<string, string> = {
  draft: 'bg-surface-sunken text-text-sub',
  submitted: 'bg-amber-50 text-amber-800',
  // 使い勝手フィードバック⑤a: 却下・取下げ (終端) と区別できる注意喚起色にする (回復可能な状態)。
  returned: 'bg-alert-bg text-alert-text',
  approved: 'bg-sky-50 text-sky-800',
  applied: 'bg-emerald-50 text-emerald-800',
  rejected: 'bg-zinc-100 text-zinc-700',
  withdrawn: 'bg-zinc-100 text-zinc-700',
  apply_failed: 'bg-alert-bg text-alert-text',
};

const CATEGORY_LABEL: Record<string, string> = {
  statutory_leave: '法定休暇', discretionary_leave: '法定外休暇',
  work_adjustment: '勤務調整', personal_info_change: '個人情報変更', other: 'その他',
};

/** ⚠️ サーバーの lib/request-instances/constants.ts の CATEGORY_PERMISSION_FEATURES と対応させること。
 *  家族フィールドの有無による employee_family への切替はサーバー側の判定であり、クライアントは
 *  ボタンの出し分けにしか使わないため category だけの近似で足りる (最終判定は必ずサーバー)。 */
const CATEGORY_FEATURE: Record<string, string> = {
  statutory_leave: 'leave', discretionary_leave: 'leave',
  work_adjustment: 'work_adjustment', personal_info_change: 'personal_info_change',
  other: 'request_instance_other',
};

/** 使い勝手フィードバック増分⑦ (D-U37): 申請書一覧を CATEGORY_LABEL の定義順でグループ化する。
 *  各カテゴリ内の順序は availableTypes が既に sort_order 昇順で返る前提をそのまま維持する
 *  (新規テーブル・新規列は不要、`available-types` は既に sort_order 昇順で返している)。 */
const groupAvailableTypesByCategory = (types: AvailableType[]): Array<{ category: string; types: AvailableType[] }> => {
  const byCategory = new Map<string, AvailableType[]>();
  for (const t of types) {
    const list = byCategory.get(t.category) ?? [];
    list.push(t);
    byCategory.set(t.category, list);
  }
  // ⚠️ QAレビュー指摘 (MEDIUM): CATEGORY_LABEL に無い category (将来カタログ追加時の未同期等、
  // §8-93/§8-152 型) を黙って落とすと、その申請種別が新規申請ダイアログから選べなくなる。
  // 未知カテゴリも末尾に残す (他の参照箇所と同じ `CATEGORY_LABEL[x] ?? x` フォールバックに揃える)。
  const known = Object.keys(CATEGORY_LABEL).filter((c) => byCategory.has(c));
  const unknown = [...byCategory.keys()].filter((c) => !CATEGORY_LABEL[c]);
  return [...known, ...unknown].map((category) => ({ category, types: byCategory.get(category)! }));
};

const StatusBadge = ({ status }: { status: string }) => (
  <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${STATUS_CLS[status] ?? 'bg-surface-sunken text-text-sub'}`}>
    {STATUS_LABEL[status] ?? status}
  </span>
);

const errorMessage = (e: unknown, fallback: string) =>
  e instanceof ApiClientError ? e.message : fallback;

export function RequestsPage() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('all');
  const [creating, setCreating] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  // 承認画面 (/approvals) の「申請内容を確認する」からの遷移で該当申請を直接開く
  const [searchParams, setSearchParams] = useSearchParams();
  const openParam = searchParams.get('open');
  useEffect(() => {
    if (!openParam) return;
    setDetailId(openParam);
    searchParams.delete('open');
    setSearchParams(searchParams, { replace: true });
  }, [openParam, searchParams, setSearchParams]);

  const { data: requests = [], isLoading, isError } = useQuery<RequestRow[]>({
    queryKey: ['request-instances', statusFilter],
    queryFn: async () => {
      const q = statusFilter === 'all' ? '' : `?status=${encodeURIComponent(statusFilter)}`;
      const res = await apiFetch<{ requests: RequestRow[] }>(`/request-instances${q}`);
      return res.requests;
    },
  });

  const { data: availableTypes = [] } = useQuery<AvailableType[]>({
    queryKey: ['request-available-types'],
    queryFn: async () => {
      const res = await apiFetch<{ request_types: AvailableType[] }>('/request-instances/available-types');
      return res.request_types;
    },
  });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['request-instances'] });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-bold text-text-body">申請</h1>
          <p className="text-sm text-text-sub mt-0.5">勤務調整・個人情報変更などの申請を作成・確認します。</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">すべての状態</SelectItem>
              {Object.entries(STATUS_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="primary" leftIcon={<Plus className="h-4 w-4" />}
            disabled={availableTypes.length === 0} onClick={() => setCreating(true)}>
            新規申請
          </Button>
        </div>
      </div>

      {availableTypes.length === 0 && (
        <p className="text-sm text-text-sub">
          現在申請できる種別がありません。申請種別の設定 (設定 &gt; 申請種別) をご確認ください。
        </p>
      )}

      {isLoading ? (
        <p className="text-text-sub">読み込み中…</p>
      ) : isError ? (
        <p className="text-sm text-alert-text">申請一覧の取得に失敗しました</p>
      ) : requests.length === 0 ? (
        <p className="text-sm text-text-sub">該当する申請はありません。</p>
      ) : (
        <>
          {/* モバイル: カード */}
          <ul className="space-y-2 md:hidden">
            {requests.map((r) => (
              <li key={r.request_instance_id}>
                <button type="button" onClick={() => setDetailId(r.request_instance_id)}
                  className="w-full text-left border border-surface-border rounded-lg bg-white p-3 min-h-[44px]">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-text-body">{r.request_type_label ?? '(不明な申請種別)'}</span>
                    <StatusBadge status={r.status} />
                  </div>
                  <p className="text-xs text-text-sub mt-1">
                    {CATEGORY_LABEL[r.request_type_category ?? ''] ?? r.request_type_category} ／ {r.created_at.slice(0, 10)}
                  </p>
                  {r.apply_error && <p className="text-xs text-alert-text mt-1">{r.apply_error}</p>}
                </button>
              </li>
            ))}
          </ul>

          {/* デスクトップ: テーブル */}
          <div className="hidden md:block overflow-x-auto border border-surface-border rounded-lg bg-white">
            <table className="w-full text-sm">
              <thead className="bg-surface-sunken">
                <tr>
                  <th scope="col" className="text-left px-3 py-2 font-medium text-text-sub">申請種別</th>
                  <th scope="col" className="text-left px-3 py-2 font-medium text-text-sub">区分</th>
                  <th scope="col" className="text-left px-3 py-2 font-medium text-text-sub">状態</th>
                  <th scope="col" className="text-left px-3 py-2 font-medium text-text-sub">作成日</th>
                  <th scope="col" className="text-left px-3 py-2 font-medium text-text-sub">更新日</th>
                  <th scope="col" className="text-right px-3 py-2 font-medium text-text-sub">操作</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((r) => (
                  <tr key={r.request_instance_id} className="border-t border-surface-border">
                    <td className="px-3 py-2 text-text-body">{r.request_type_label ?? '(不明な申請種別)'}</td>
                    <td className="px-3 py-2 text-text-sub">{CATEGORY_LABEL[r.request_type_category ?? ''] ?? r.request_type_category}</td>
                    <td className="px-3 py-2">
                      <StatusBadge status={r.status} />
                      {r.apply_error && <p className="text-xs text-alert-text mt-1 max-w-md">{r.apply_error}</p>}
                    </td>
                    <td className="px-3 py-2 text-text-sub tabular-nums">{r.created_at.slice(0, 10)}</td>
                    <td className="px-3 py-2 text-text-sub tabular-nums">{r.updated_at.slice(0, 10)}</td>
                    <td className="px-3 py-2 text-right">
                      <Button variant="ghost" size="sm" onClick={() => setDetailId(r.request_instance_id)}>詳細</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {creating && (
        <NewRequestModal availableTypes={availableTypes}
          onClose={() => setCreating(false)}
          onRefreshList={refresh}
          onDone={() => { setCreating(false); refresh(); }} />
      )}
      {detailId && (
        <RequestDetailModal requestInstanceId={detailId}
          onClose={() => setDetailId(null)}
          onChanged={refresh} />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
function NewRequestModal({ availableTypes, onClose, onDone, onRefreshList }: {
  availableTypes: AvailableType[]; onClose: () => void; onDone: () => void; onRefreshList: () => void;
}) {
  const [requestTypeId, setRequestTypeId] = useState(availableTypes[0]?.request_type_id ?? '');
  const [values, setValues] = useState<FormValues>({});
  // 使い勝手フィードバック⑥ (D-U28): クライアント側バリデーションの結果 (blur 表示用) と、
  // 提出を試みたか (true にすると未 blur の項目のエラーも含めて全件表示する)。
  const [fieldErrors, setFieldErrors] = useState<Map<string, string>>(new Map());
  const [showAllErrors, setShowAllErrors] = useState(false);

  const { data: spec, isLoading } = useQuery<FormSpecResponse>({
    queryKey: ['request-form', requestTypeId],
    queryFn: () => apiFetch<FormSpecResponse>(`/request-instances/form?request_type_id=${encodeURIComponent(requestTypeId)}`),
    enabled: !!requestTypeId,
  });

  // 申請種別を切り替えたら入力値と下書きをリセットする (フィールド定義が別物になるため持ち越さない)
  useEffect(() => { setValues({}); setDraftId(null); setDraftVersion(1); setShowAllErrors(false); }, [requestTypeId]);

  // ⚠️ 5並列レビュー Major (QA/design-and-code): 提出のたびに新しい下書きを作ると、必須未入力等で
  // 400 になるたびに下書きが増殖し、しかもモーダルが閉じて入力内容が失われる。
  // 下書きは1回だけ作って id を保持し、以後は PUT で上書きしてから提出する。
  // 失敗時はモーダルも入力値も維持し、ユーザーが直して再送できるようにする。
  const [draftId, setDraftId] = useState<string | null>(null);
  const [draftVersion, setDraftVersion] = useState(1);

  /** 下書きを作成 or 更新し、最新の (id, row_version) を返す。 */
  const persistDraft = async (): Promise<{ id: string; rowVersion: number }> => {
    if (!draftId) {
      const created = await apiFetch<{ request_instance_id: string }>('/request-instances', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ request_type_id: requestTypeId, values }),
      });
      setDraftId(created.request_instance_id);
      setDraftVersion(1);
      return { id: created.request_instance_id, rowVersion: 1 };
    }
    const updated = await apiFetch<{ row_version: number }>(`/request-instances/${draftId}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ row_version: draftVersion, values }),
    });
    setDraftVersion(updated.row_version);
    return { id: draftId, rowVersion: updated.row_version };
  };

  // 使い勝手フィードバック増分⑧ (design-expert H-5): 未保存の新規申請でも添付操作の時点で
  // 下書きを自動作成する (「先に保存してください」と言わせない)。
  const { attachmentsByField, refresh: refreshAttachments } = useAttachmentsQuery(draftId);
  // ⚠️ QAレビュー指摘 (MEDIUM): draftId が null の間に複数の file 型フィールドへほぼ同時に
  // アップロードすると、各々の ensureRequestInstanceId() が draftId===null を見て個別に
  // persistDraft() (POST /request-instances) を呼び、下書きが二重に作られるレースがある
  // (setDraftId は最後の呼び出しが勝ち、片方の添付はどの下書きにも紐付かない孤児になる)。
  // 進行中の Promise を ref にキャッシュし、後続の呼び出しは同じ Promise を待つことで一本化する。
  const pendingDraftCreation = useRef<Promise<{ id: string; rowVersion: number }> | null>(null);
  const ensureRequestInstanceId = async (): Promise<string> => {
    if (draftId) return draftId;
    if (!pendingDraftCreation.current) {
      pendingDraftCreation.current = persistDraft().finally(() => { pendingDraftCreation.current = null; });
    }
    return (await pendingDraftCreation.current).id;
  };
  const attachmentCallbacks: AttachmentCallbacks = {
    requestInstanceId: draftId,
    ensureRequestInstanceId,
    uploadAttachment: uploadRequestAttachment,
    deleteAttachment: deleteRequestAttachment,
    fetchAttachmentBlob: fetchRequestAttachmentBlob,
    onAttachmentsChanged: refreshAttachments,
  };

  const submitMutation = useMutation({
    mutationFn: async (mode: 'draft' | 'submit') => {
      const draft = await persistDraft();
      if (mode === 'draft') return { mode, status: 'draft' as const };
      const res = await apiFetch<{ status: string; apply_error: string | null }>(
        `/request-instances/${draft.id}/submit`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ row_version: draft.rowVersion }) },
      );
      return { mode, status: res.status, applyError: res.apply_error };
    },
    onSuccess: (r) => {
      if (r.mode === 'draft') toast.success('下書きを保存しました');
      else if (r.status === 'submitted') toast.success('申請を提出しました（承認待ち）');
      else if (r.status === 'apply_failed') toast.warning(`申請は記録されましたが反映に失敗しました: ${r.applyError ?? ''}`);
      else toast.success('申請を提出しました');
      onDone();
    },
    onError: (e) => {
      // モーダルは閉じない (入力値を維持して修正・再送できるようにする)。一覧だけ最新化する。
      toast.error(errorMessage(e, '申請の保存に失敗しました'));
      onRefreshList();
    },
  });

  const selected = availableTypes.find((t) => t.request_type_id === requestTypeId);

  return (
    <Modal open onClose={onClose} title="新規申請">
      <div className="space-y-4">
        <div>
          <label htmlFor="rq-type" className="block text-sm text-text-body mb-1">申請種別</label>
          <Select value={requestTypeId} onValueChange={setRequestTypeId}>
            <SelectTrigger id="rq-type" className="w-full min-h-[44px]"><SelectValue placeholder="選択してください" /></SelectTrigger>
            <SelectContent>
              {groupAvailableTypesByCategory(availableTypes).map((g) => (
                <SelectGroup key={g.category}>
                  <SelectLabel>{CATEGORY_LABEL[g.category] ?? g.category}</SelectLabel>
                  {g.types.map((t) => (
                    <SelectItem key={t.request_type_id} value={t.request_type_id}>{t.label}</SelectItem>
                  ))}
                </SelectGroup>
              ))}
            </SelectContent>
          </Select>
          {selected && (
            <p className="text-xs text-text-sub mt-1">
              {CATEGORY_LABEL[selected.category] ?? selected.category}
              {selected.requires_approval ? ' ／ 提出後に承認が必要です' : ' ／ 提出すると即時に確定します'}
            </p>
          )}
        </div>

        {isLoading ? (
          <p className="text-text-sub text-sm">入力項目を読み込み中…</p>
        ) : spec ? (
          <RequestFormFields fields={spec.fields} conditions={spec.conditions} formulas={spec.formulas}
            values={values} onChange={setValues}
            onResolveWorkDays={(start, end) => resolveWorkDays(start, end)}
            showAllErrors={showAllErrors} onValidationChange={setFieldErrors}
            attachmentsByField={attachmentsByField} attachments={attachmentCallbacks} />
        ) : null}

        <div className="flex flex-wrap justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>キャンセル</Button>
          {/* 下書き保存は途中入力を許すため、クライアント側バリデーションでは止めない (D-U19 と同じ判断)。 */}
          <Button variant="secondary" disabled={!requestTypeId || submitMutation.isPending}
            onClick={() => submitMutation.mutate('draft')}>下書き保存</Button>
          {/* 使い勝手フィードバック⑥ (D-U28): 提出時に client 側で分かっている不備があれば、
              まず全件表示 (showAllErrors) してブロックする。最終判定は必ずサーバーへ委ねる (§7-4)。 */}
          <Button variant="primary" disabled={!requestTypeId || submitMutation.isPending}
            loading={submitMutation.isPending}
            onClick={() => { setShowAllErrors(true); if (fieldErrors.size === 0) submitMutation.mutate('submit'); }}>
            提出する
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// -----------------------------------------------------------------------------
function RequestDetailModal({ requestInstanceId, onClose, onChanged }: {
  requestInstanceId: string; onClose: () => void; onChanged: () => void;
}) {
  const [values, setValues] = useState<FormValues | null>(null);
  const [confirmWithdraw, setConfirmWithdraw] = useState(false);
  // 使い勝手フィードバック⑥ (D-U28): NewRequestModal と同じ (§7-4)。
  const [fieldErrors, setFieldErrors] = useState<Map<string, string>>(new Map());
  const [showAllErrors, setShowAllErrors] = useState(false);

  const { data, isLoading, isError, refetch } = useQuery<DetailResponse>({
    queryKey: ['request-instance', requestInstanceId],
    queryFn: () => apiFetch<DetailResponse>(`/request-instances/${requestInstanceId}`),
  });

  useEffect(() => { if (data) { setValues(data.values); setShowAllErrors(false); } }, [data]);

  // 使い勝手フィードバック増分⑧ (D-U26/D-U31): この画面は既存の申請を開いているため
  // requestInstanceId は常に確定済み (ensureRequestInstanceId は自明)。
  const { attachmentsByField, refresh: refreshAttachments } = useAttachmentsQuery(requestInstanceId);
  const attachmentCallbacks: AttachmentCallbacks = {
    requestInstanceId,
    ensureRequestInstanceId: async () => requestInstanceId,
    uploadAttachment: uploadRequestAttachment,
    deleteAttachment: deleteRequestAttachment,
    fetchAttachmentBlob: fetchRequestAttachmentBlob,
    onAttachmentsChanged: refreshAttachments,
  };

  const isDraft = data?.request.status === 'draft';
  // 使い勝手フィードバック⑤a (D-U14): 'returned' (申請者まで差し戻された状態) も編集可能。
  const isReturned = data?.request.status === 'returned';
  const isEditableStatus = isDraft || isReturned;
  // ⚠️ 5並列レビュー Medium (QA/design-and-code): manage は all_stores/node_scope の行しか無いため
  // 申請者本人 (self のみ保有) には必ず 403 になる。押せるのに必ず失敗するボタンを出さない (F-4/D-209 と同型)。
  const user = useAuthStore((st) => st.user);
  const canRetry = hasPermission(
    user?.effective_permissions,
    CATEGORY_FEATURE[data?.request.request_type_category ?? ''] ?? 'request_instance_other',
    'manage', user?.non_inheriting_features,
  );
  // ⚠️ TD-28 §17-2: 反映後の取下げは `delete` で判定される。押せるのに必ず 403 になるボタンを
  //    出さない (canRetry と同じ判断、F-4/D-209 と同型)。
  const canDeleteAfterApply = hasPermission(
    user?.effective_permissions,
    CATEGORY_FEATURE[data?.request.request_type_category ?? ''] ?? 'request_instance_other',
    'delete', user?.non_inheriting_features,
  );
  // ⚠️ 個人情報変更は反映済みからの取消をサーバーが拒否する (SCD-2 の巻き戻しは別の変更まで
  //    消すため、§17-2 の表)。押した後に 409 で失敗させず最初から出さない。
  const isPersonalInfo = data?.request.request_type_category === 'personal_info_change';
  const canWithdrawApplied = canDeleteAfterApply
    && !(isPersonalInfo && data?.request.status === 'applied');

  const saveMutation = useMutation({
    mutationFn: () => apiFetch(`/request-instances/${requestInstanceId}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ row_version: data!.request.row_version, values: values ?? {} }),
    }),
    onSuccess: () => { toast.success('下書きを保存しました'); void refetch(); onChanged(); },
    onError: (e) => toast.error(errorMessage(e, '保存に失敗しました')),
  });

  const submitMutation = useMutation({
    mutationFn: async () => {
      // 未保存の編集内容を先に保存してから提出する (下書き画面から直接提出できるようにする)
      await apiFetch(`/request-instances/${requestInstanceId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ row_version: data!.request.row_version, values: values ?? {} }),
      });
      return apiFetch<{ status: string; apply_error: string | null }>(
        `/request-instances/${requestInstanceId}/submit`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ row_version: data!.request.row_version + 1 }) },
      );
    },
    onSuccess: (res) => {
      if (res.status === 'apply_failed') toast.warning(`申請は記録されましたが反映に失敗しました: ${res.apply_error ?? ''}`);
      else toast.success('申請を提出しました');
      void refetch(); onChanged();
    },
    onError: (e) => { toast.error(errorMessage(e, '提出に失敗しました')); void refetch(); },
  });

  const withdrawMutation = useMutation({
    mutationFn: () => apiFetch(`/request-instances/${requestInstanceId}/withdraw`, { method: 'POST' }),
    onSuccess: () => { toast.success('申請を取下げました'); setConfirmWithdraw(false); void refetch(); onChanged(); },
    onError: (e) => { toast.error(errorMessage(e, '取下げに失敗しました')); setConfirmWithdraw(false); },
  });

  // TD-28 §18-1: 時季変更の申入れ (使用者側) と応答 (本人側)。
  const [timingChangeOpen, setTimingChangeOpen] = useState(false);
  // ⚠️ design-expert Critical-3: 承諾は休暇日を振り替える不可逆操作、辞退も恒久。
  //    取下げと同様に確認を挟む (押した側だけを loading にするため保留中の応答も保持する)。
  const [pendingResponse, setPendingResponse] = useState<'accepted' | 'declined' | null>(null);
  const pendingTimingChange = data?.timing_changes.find((t) => t.employee_response === null) ?? null;
  // 応答できるのは本人だけ。サーバーが本人性で判定するため、UI も self 文脈のときだけ出す。
  const canRespondTimingChange = !!pendingTimingChange && data?.display_context === 'self';

  const respondTimingChangeMutation = useMutation({
    mutationFn: (response: 'accepted' | 'declined') => apiFetch<{ status: string }>(
      `/request-instances/${requestInstanceId}/timing-change/${pendingTimingChange!.change_request_id}/respond`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ response }) },
    ),
    onSuccess: (res, response) => {
      if (response === 'declined') toast.success('時季変更を辞退しました。元の申請のまま成立します');
      else if (res.status === 'applied') toast.success('時季変更を承諾しました。休暇の日程を振り替えました');
      else toast.warning('承諾しましたが振り替えの反映に失敗しました。管理者に連絡してください');
      setPendingResponse(null);
      void refetch(); onChanged();
    },
    onError: (e) => {
      toast.error(errorMessage(e, '応答に失敗しました'));
      setPendingResponse(null);
      void refetch();
    },
  });

  const retryMutation = useMutation({
    mutationFn: () => apiFetch<{ status: string; apply_error: string | null }>(
      `/request-instances/${requestInstanceId}/retry-apply`, { method: 'POST' }),
    onSuccess: (res) => {
      if (res.status === 'applied') toast.success('反映しました');
      else toast.error(`再実行しましたが失敗しました: ${res.apply_error ?? ''}`);
      void refetch(); onChanged();
    },
    onError: (e) => toast.error(errorMessage(e, '再実行に失敗しました')),
  });

  return (
    <>
      <Modal open onClose={onClose} title="申請の詳細">
        {isLoading ? (
          <p className="text-text-sub text-sm">読み込み中…</p>
        ) : isError || !data ? (
          <p className="text-sm text-alert-text">申請の取得に失敗しました</p>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-text-body">{data.request.request_type_label ?? '(不明な申請種別)'}</span>
              <StatusBadge status={data.request.status} />
            </div>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-text-sub">
              {data.request.submitted_at && (<><dt>提出</dt><dd className="tabular-nums">{data.request.submitted_at.slice(0, 16).replace('T', ' ')}</dd></>)}
              {data.request.decided_at && (<><dt>決定</dt><dd className="tabular-nums">{data.request.decided_at.slice(0, 16).replace('T', ' ')}</dd></>)}
              {data.request.applied_at && (<><dt>反映</dt><dd className="tabular-nums">{data.request.applied_at.slice(0, 16).replace('T', ' ')}</dd></>)}
            </dl>

            {data.request.apply_error && (
              <div role="alert" className="rounded-md bg-alert-bg text-alert-text text-sm px-3 py-2">
                反映に失敗しました: {data.request.apply_error}
              </div>
            )}

            {/* 使い勝手フィードバック⑤a (B5-03、D-U20/D-U34): 差し戻し理由を読み取り専用で目立たせる。
                差し戻し回数も併せて表示する (D-U34)。return_reason フィールド自体は下の
                RequestFormFields にも編集不可として現れるため、こちらは要点を目立たせる補助表示。 */}
            {isReturned && (
              // design-expert MEDIUM-1 (増分⑤b実機検証で反映): 下の「前段階へ戻された」バナーと
              // 背景色・文字色が同一で見分けが付かなかった。こちらは「本人が編集・再提出すべき」
              // 状態のためアイコンで能動的なアクションを示す (Undo2)。
              <div role="alert" className="rounded-md bg-alert-bg text-alert-text text-sm px-3 py-2 flex items-start gap-2">
                <Undo2 size={16} className="shrink-0 mt-0.5" aria-hidden />
                <div className="space-y-1">
                  <p className="font-medium">差し戻されました{data.request.return_count > 0 && `（${data.request.return_count}回目）`}</p>
                  {(() => {
                    const returnReasonField = data.fields.find((f) => f.semantic_role === 'return_reason');
                    const reasonValue = returnReasonField ? (values ?? data.values)[returnReasonField.field_definition_id] : null;
                    return typeof reasonValue === 'string' && reasonValue
                      ? <p>{returnReasonField?.label ?? '差し戻し理由'}：{reasonValue}</p>
                      : null;
                  })()}
                </div>
              </div>
            )}
            {/* 使い勝手フィードバック⑤b (D-U14 ③-b/D-U24/D-U34): 前段階へ戻す経路は status が
                'submitted' のまま変わらないため、isReturned の分岐だけでは何も表示されない。
                内容は編集できない (D-U24) が、審査が前の段階へ差し戻されたことと理由は本人にも
                分かるようにする。
                ⚠️ QA Major/platform-reviewer HIGH/design-and-code M-1 (3レビュアー独立検出): 判定条件を
                return_count>0 (生涯累積カウンタ、リセットされない) から is_level_back_pending
                (今まさに戻された段階で再決裁待ちか) へ変更。旧条件では③-a経路の再提出後や、
                戻された段階の承認者が再承認して先へ進んだ後も、古いバナーが表示され続けていた。 */}
            {data.request.status === 'submitted' && data.request.is_level_back_pending && (
              // design-expert MEDIUM-1: 上の「差し戻されました」バナー (本人が編集すべき) との
              // 混同防止。こちらは「他の承認者が再確認中」という受動的な状態のため、
              // 巻き戻しを示す中立的なアイコン (RotateCcw) にする。
              <div role="status" className="rounded-md bg-alert-bg text-alert-text text-sm px-3 py-2 flex items-start gap-2">
                <RotateCcw size={16} className="shrink-0 mt-0.5" aria-hidden />
                <div className="space-y-1">
                  <p className="font-medium">前の承認段階へ差し戻されました（{data.request.return_count}回目）。内容は変更できません</p>
                  {(() => {
                    const returnReasonField = data.fields.find((f) => f.semantic_role === 'return_reason');
                    const reasonValue = returnReasonField ? (values ?? data.values)[returnReasonField.field_definition_id] : null;
                    return typeof reasonValue === 'string' && reasonValue
                      ? <p>{returnReasonField?.label ?? '差し戻し理由'}：{reasonValue}</p>
                      : null;
                  })()}
                </div>
              </div>
            )}

            {/* ⚠️ TD-28 D-25 必須要件①: 隠した項目があることを必ず伝える。黙って消すと、
                承認者は全項目を見た上で承認したつもりになる。 */}
            {(data.hidden_field_count > 0 || data.masked_field_ids.length > 0) && (
              <div className="rounded-md border border-surface-border bg-surface-sunken text-text-sub text-sm px-3 py-2">
                {CONTEXT_LABEL[data.display_context] ?? ''}
                {data.display_context === 'approver' && data.approval_level > 0 && `（承認段階 ${data.approval_level}）`}
                ：
                {/* ⚠️ 「表示制御により」と限定する。下書きでは条件付き表示 (hide) で隠れる項目もあり、
                    件数の内訳を断定できないため (5並列レビュー Minor)。 */}
                {data.hidden_field_count > 0 && `表示制御により、この画面では見られない項目が ${data.hidden_field_count} 件あります。`}
                {data.masked_field_ids.length > 0 && `内容を閲覧できない項目が ${data.masked_field_ids.length} 件あります。`}
              </div>
            )}

            <RequestFormFields fields={data.fields} conditions={data.conditions} formulas={data.formulas}
              values={values ?? data.values} onChange={setValues} readOnly={!isEditableStatus}
              maskedFieldIds={data.masked_field_ids}
              // 使い勝手フィードバック⑤a (D-U20): return_reason は決裁者がサーバー経由で書き込む項目
              // であり、可視性設定に関わらず申請者は編集できない (PUT /:id も無条件で上書きを拒む)。
              nonEditableFieldIds={[
                ...data.non_editable_field_ids,
                ...data.fields.filter((f) => f.semantic_role === 'return_reason').map((f) => f.field_definition_id),
              ]}
              onResolveWorkDays={isEditableStatus
                ? (start, end) => resolveWorkDays(start, end, data.request.requester_employee_id)
                : undefined}
              showAllErrors={showAllErrors} onValidationChange={setFieldErrors}
              attachmentsByField={attachmentsByField} attachments={attachmentCallbacks} />

            {/* TD-28 §18-1: 時季変更の申入れ (労基法39条5項)。使用者は申し入れるだけで、
                成立させるかは本人の承諾による (§13-2②: 却下の口は持たない)。 */}
            {data.timing_changes.length > 0 && (
              <section className="rounded-md border border-surface-border bg-surface-sunken px-3 py-2 space-y-2">
                <h3 className="text-sm font-medium text-text-body">時季変更の申入れ</h3>
                <ul className="space-y-2">
                  {data.timing_changes.map((t) => (
                    <li key={t.change_request_id} className="text-xs text-text-sub space-y-1">
                      <p className="tabular-nums">
                        {t.requested_at.slice(0, 10)} 申入れ：
                        {t.proposed_start_date ?? '—'} 〜 {t.proposed_end_date ?? '—'} へ振り替え
                      </p>
                      <p>理由：{t.business_reason}</p>
                      {/* ⚠️ design-expert Major-4: 状態を本文に埋没させない。最も要対応の
                          「応答待ち」が一番弱く見えるのを避け、色+テキストで区別する。 */}
                      <p className="flex flex-wrap items-center gap-1.5">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                          t.employee_response === null ? 'bg-alert-bg text-alert-text'
                            : t.employee_response === 'accepted' ? 'bg-emerald-50 text-emerald-800'
                              : 'bg-surface-sunken text-text-body'}`}>
                          {t.employee_response === null ? '応答待ち'
                            : t.employee_response === 'accepted' ? '承諾済み' : '辞退'}
                        </span>
                        {t.responded_at && <span className="tabular-nums">{t.responded_at.slice(0, 10)}</span>}
                        {t.response_note ? <span>：{t.response_note}</span> : null}
                      </p>
                    </li>
                  ))}
                </ul>
                {canRespondTimingChange && (
                  <div className="flex flex-wrap gap-2 pt-1">
                    {/* ⚠️ 押した側だけを loading にする (両方回すと何を実行中か分からない) */}
                    <Button variant="secondary"
                      loading={respondTimingChangeMutation.isPending && pendingResponse === 'declined'}
                      disabled={respondTimingChangeMutation.isPending}
                      onClick={() => setPendingResponse('declined')}>辞退する</Button>
                    <Button variant="primary"
                      loading={respondTimingChangeMutation.isPending && pendingResponse === 'accepted'}
                      disabled={respondTimingChangeMutation.isPending}
                      onClick={() => setPendingResponse('accepted')}>承諾する</Button>
                  </div>
                )}
              </section>
            )}

            <div className="flex flex-wrap justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={onClose}>閉じる</Button>
              {/* 申入れは leave:manage 保有者のみ。押せるのに必ず 403 になるボタンを出さない
                  (canRetry と同じ判断、F-4/D-209 と同型)。 */}
              {data.can_request_timing_change && canRetry && (
                <Button variant="secondary" onClick={() => setTimingChangeOpen(true)}>時季変更を申し入れる</Button>
              )}
              {/* ⚠️ TD-28 §17-2 (D-31): 反映済み・反映失敗からも取り下げられる (休暇なら消化が戻る)。
                  使い勝手フィードバック⑤a (I5-21): 'returned' からも取下げ可能。 */}
              {((data.request.status === 'draft' || data.request.status === 'submitted' || isReturned)
                || ((data.request.status === 'applied' || data.request.status === 'apply_failed')
                  && canWithdrawApplied)) && (
                <Button variant="secondary" onClick={() => setConfirmWithdraw(true)}>取下げ</Button>
              )}
              {isPersonalInfo && data.request.status === 'applied' && (
                <p className="w-full text-xs text-text-sub">
                  反映済みの個人情報変更は取り下げられません。内容を戻す場合は社員情報の画面から修正してください。
                </p>
              )}
              {data.request.status === 'apply_failed' && canRetry && (
                <Button variant="secondary" loading={retryMutation.isPending}
                  onClick={() => retryMutation.mutate()}>反映を再実行</Button>
              )}
              {isDraft && (
                <>
                  {/* 下書き保存は途中入力を許すため、クライアント側バリデーションでは止めない。 */}
                  <Button variant="secondary" loading={saveMutation.isPending}
                    onClick={() => saveMutation.mutate()}>下書き保存</Button>
                  {/* 使い勝手フィードバック⑥ (D-U28): 提出時に client 側で分かっている不備があれば
                      まず全件表示してブロックする。最終判定は必ずサーバーへ委ねる (§7-4)。 */}
                  <Button variant="primary" loading={submitMutation.isPending}
                    onClick={() => { setShowAllErrors(true); if (fieldErrors.size === 0) submitMutation.mutate(); }}>
                    提出する
                  </Button>
                </>
              )}
              {/* 使い勝手フィードバック⑤a (B5-04): 差し戻された申請は「修正して再提出」の1ボタンにする
                  (下書き保存の概念は無い — 既に一度提出されている申請のため)。submitMutation は
                  PUT→POST /submit の順で送るため、内容編集後にそのまま呼べる。 */}
              {isReturned && (
                <>
                  <Button variant="primary" loading={submitMutation.isPending}
                    onClick={() => { setShowAllErrors(true); if (fieldErrors.size === 0) submitMutation.mutate(); }}>
                    修正して再提出
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </Modal>
      <ConfirmDialog open={pendingResponse !== null}
        title={pendingResponse === 'accepted' ? '時季変更を承諾しますか？' : '時季変更を辞退しますか？'}
        message={pendingResponse === 'accepted'
          ? `休暇の日程が ${pendingTimingChange?.proposed_start_date ?? ''} 〜 ${pendingTimingChange?.proposed_end_date ?? ''} へ振り替えられます。承諾すると元に戻せません。`
          : '元の申請の日程のまま成立します。辞退の記録は残ります。'}
        confirmLabel={pendingResponse === 'accepted' ? '承諾する' : '辞退する'}
        loading={respondTimingChangeMutation.isPending}
        onConfirm={() => respondTimingChangeMutation.mutate(pendingResponse!)}
        onClose={() => setPendingResponse(null)} />
      {timingChangeOpen && data && (
        <TimingChangeModal requestInstanceId={requestInstanceId}
          onClose={() => setTimingChangeOpen(false)}
          onDone={() => { setTimingChangeOpen(false); void refetch(); onChanged(); }} />
      )}
      <ConfirmDialog open={confirmWithdraw} title="申請を取下げますか？"
        message={data?.request.status === 'applied'
          ? '反映済みの内容も取り消されます（休暇の場合は消化した日数が残数に戻ります）。取下げた申請は元に戻せません。'
          : data?.request.status === 'apply_failed'
            ? '反映に失敗した際に途中まで書き込まれた内容があれば、あわせて取り消します。取下げた申請は元に戻せません。'
            : '取下げた申請は元に戻せません。必要な場合は新しく作成してください。'}
        confirmLabel="取下げる" destructive loading={withdrawMutation.isPending}
        onConfirm={() => withdrawMutation.mutate()} onClose={() => setConfirmWithdraw(false)} />
    </>
  );
}

/**
 * TD-28 §18-1: 時季変更の申入れ (労基法39条5項)。
 *
 * ⚠️ 「却下」ではない (§13-2②)。使用者は時季の変更を申し入れるだけで、成立させるかは本人が決める。
 *    辞退された場合は元の申請がそのまま成立するため、この画面に却下相当の操作は置かない。
 * ⚠️ 振り替え先の所定労働日数が現在の消化日数と一致しない場合はサーバーが 409 で弾く
 *    (時季変更は同じ日数を別の日に振り替える操作であり、残数を増減させてはならないため)。
 */
/** ⚠️ design-expert Critical-1: 生の input/textarea は実高 ≈38px でタップ領域 44px を満たさず、
 *  `bg-surface` は未定義トークンで背景が当たらない。RequestFormFields.tsx の inputCls と同一にする。 */
const timingInputCls = 'w-full min-h-[44px] sm:min-h-[38px] rounded-md border border-surface-border px-3 py-2 text-sm text-text-body bg-white disabled:bg-surface-sunken disabled:text-text-sub';

function TimingChangeModal({ requestInstanceId, onClose, onDone }: {
  requestInstanceId: string; onClose: () => void; onDone: () => void;
}) {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');

  const mutation = useMutation({
    mutationFn: () => apiFetch<{ change_request_id: string; proposed_dates: string[] }>(
      `/request-instances/${requestInstanceId}/timing-change`,
      {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          proposed_start_date: startDate || null,
          proposed_end_date: endDate || null,
          business_reason: reason,
        }),
      },
    ),
    onSuccess: (res) => {
      toast.success(`時季変更を申し入れました（振り替え先 ${res.proposed_dates.length} 日、本人の承諾待ち）`);
      onDone();
    },
    onError: (e) => toast.error(errorMessage(e, '申入れに失敗しました')),
  });

  const canSubmit = reason.trim().length > 0 && (startDate !== '' || endDate !== '');

  return (
    <Modal open onClose={onClose} title="時季変更を申し入れる">
      <div className="space-y-4">
        <p className="text-xs text-text-sub">
          事業の正常な運営を妨げる場合に、休暇の時季の変更を申し入れます。
          変更が成立するには本人の承諾が必要です。振り替え先の所定労働日数は、
          現在の消化日数と一致している必要があります。
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="space-y-1">
            <span className="block text-sm text-text-body">振り替え先の開始日</span>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
              className={timingInputCls} />
          </label>
          <label className="space-y-1">
            <span className="block text-sm text-text-body">振り替え先の終了日</span>
            <input type="date" value={endDate} min={startDate || undefined}
              onChange={(e) => setEndDate(e.target.value)} className={timingInputCls} />
          </label>
        </div>
        <p className="text-xs text-text-sub">
          いずれか一方だけの指定もできます。その場合、休暇の期間の長さを保ったまま、もう一方の日付が
          自動的にずれます（1日の休暇なら1日のまま移動します）。
        </p>
        <label className="space-y-1 block">
          <span className="block text-sm text-text-body">
            事業運営上の理由 <span className="text-alert-text">必須</span>
          </span>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={1000}
            className={timingInputCls} />
        </label>
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>キャンセル</Button>
          <Button variant="primary" disabled={!canSubmit} loading={mutation.isPending}
            onClick={() => mutation.mutate()}>申し入れる</Button>
          {!canSubmit && (
            <p className="w-full text-right text-xs text-text-sub">
              振り替え先の日付（いずれか一方）と理由の入力が必要です。
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
}
